/**
 * Cutting a stretch of coverage into shifts.
 *
 * The planner aims for the shift length it was asked for, but a shift only
 * counts if somebody could lawfully work it: it has to fit the longest shift
 * the rule allows, and the break it is owed has to fit before midnight, where
 * the roster can hold one. So the cut that comes closest to the preferred
 * length among the lawful ones wins, and a 12-hour night may stay one shift
 * where two six-hour halves would leave the second with nowhere for its break.
 */

import { planBreakWindow } from '../rosterCompliance/breakPlanner';
import { requiredBreakMinutes, windowMinutes } from '../rosterCompliance/rosterModel';
import type { NightWorkRule, ScheduleRule, TimeWindow } from './types';

/**
 * What the planner holds itself to where the country's rule row is silent, so
 * "no limit on file" never becomes a 24-hour shift or a month without a day off.
 */
export const PLANNING_POLICY = {
  minimumRestMinutes: 660,
  maxConsecutiveDays: 6,
  maxShiftMinutes: 720,
  /** Shorter shifts are only cut when the coverage itself is shorter. */
  minimumShiftMinutes: 120,
  /** The board will not hold a duty shorter than this. */
  minimumDutyMinutes: 30,
  snapMinutes: 15,
} as const;

/** Night minutes in a window, counted the way `roster_overlap_minutes` counts them. */
export function nightMinutesOf(window: TimeWindow, night: NightWorkRule | null): number {
  if (!night) return 0;
  let total = 0;
  for (let offset = -1440; offset <= 4320; offset += 1440) {
    const start = Math.max(window.startMinute, offset + night.periodStartMinute);
    const end = Math.min(window.endMinute, offset + night.periodEndMinute);
    total += Math.max(end - start, 0);
  }
  return total;
}

export const isNightShift = (window: TimeWindow, night: NightWorkRule | null): boolean => (
  night !== null && nightMinutesOf(window, night) >= night.minimumNightMinutes
);

export const longestShiftMinutes = (rule: ScheduleRule | null): number => (
  Math.min(rule?.maxShiftMinutes ?? Number.POSITIVE_INFINITY, PLANNING_POLICY.maxShiftMinutes)
);

/** Where the break a shift is owed goes; null when it is owed none. */
export function planShiftBreak(
  shift: TimeWindow,
  rule: ScheduleRule | null,
  avoid: TimeWindow[] = [],
): TimeWindow | null {
  const owed = requiredBreakMinutes(rule, windowMinutes(shift));
  return owed > 0 ? planBreakWindow({ shift, durationMinutes: owed, avoid }) : null;
}

/**
 * Whether the law lets anybody work this shift, whoever they are: it is no
 * longer than the rule allows and the break it is owed can be placed.
 */
export function isLawfulShift(shift: TimeWindow, rule: ScheduleRule | null): boolean {
  const length = windowMinutes(shift);
  if (length <= 0 || (rule?.maxShiftMinutes && length > rule.maxShiftMinutes)) return false;

  const owed = requiredBreakMinutes(rule, length);
  if (owed > 0 && planBreakWindow({ shift, durationMinutes: owed }) === null) return false;

  const night = rule?.night ?? null;
  if (night?.maxShiftMinutes && isNightShift(shift, night) && length > night.maxShiftMinutes) return false;

  return true;
}

/** A shift the planner would cut itself: lawful, and within its own limits on length. */
export function isWorkableShift(shift: TimeWindow, rule: ScheduleRule | null): boolean {
  const length = windowMinutes(shift);
  return length >= PLANNING_POLICY.minimumDutyMinutes
    && length <= longestShiftMinutes(rule)
    && isLawfulShift(shift, rule);
}

/** `count` back-to-back shifts covering the window, as even as a 15-minute grid allows. */
function splitEvenly(window: TimeWindow, count: number): TimeWindow[] {
  const snap = PLANNING_POLICY.snapMinutes;
  const total = windowMinutes(window);
  const base = Math.floor(total / count / snap) * snap;
  const lengths = Array.from({ length: count }, () => base);

  let spare = total - (base * count);
  for (let index = 0; spare >= snap; index = (index + 1) % count) {
    lengths[index] += snap;
    spare -= snap;
  }
  lengths[count - 1] += spare;

  const shifts: TimeWindow[] = [];
  let cursor = window.startMinute;
  lengths.forEach((length) => {
    shifts.push({ startMinute: cursor, endMinute: cursor + length });
    cursor += length;
  });
  return shifts;
}

/**
 * The shifts that cover a stretch of coverage. Every one is workable where any
 * cut is; if none is, the nearest cut is returned anyway so the gap shows on
 * the draft as posts nobody could be given.
 */
export function tileWindow(
  window: TimeWindow,
  preferredShiftMinutes: number,
  rule: ScheduleRule | null,
): TimeWindow[] {
  const total = windowMinutes(window);
  if (total < PLANNING_POLICY.minimumDutyMinutes) return [];

  const preferred = Math.max(preferredShiftMinutes, PLANNING_POLICY.minimumDutyMinutes);
  const mostShifts = Math.max(1, Math.floor(total / PLANNING_POLICY.minimumShiftMinutes));

  let best: { shifts: TimeWindow[]; distance: number } | null = null;
  for (let count = 1; count <= mostShifts; count += 1) {
    const shifts = splitEvenly(window, count);
    if (!shifts.every((shift) => isWorkableShift(shift, rule))) continue;
    const distance = Math.abs((total / count) - preferred);
    // Strictly closer only: on a tie the earlier count, with fewer handovers, stands.
    if (!best || distance < best.distance) best = { shifts, distance };
  }
  if (best) return best.shifts;

  const target = Math.min(preferred, longestShiftMinutes(rule));
  return splitEvenly(window, Math.max(1, Math.min(mostShifts, Math.ceil(total / target))));
}
